import React, { useState } from "react";
import { ChevronLeft, Send, Loader2, Store, Truck, AlertTriangle, Banknote, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useOrderList } from "@/context/OrderListContext";
import {
  SERVICE_AREAS, DELIVERY_TIME_OPTIONS, PAYMENT_OPTIONS, formatPrice,
  computeTotals, DELIVERY_MIN_TOTAL, PICKUP_DISCOUNT_PCT,
} from "@/lib/site";

const inputCls =
  "w-full h-11 rounded-sm border border-copper/25 bg-stone-light px-3 text-sm text-cream placeholder:text-cream/30 focus:border-copper focus:outline-none";

export default function CheckoutForm({ site, onBack, onDone }) {
  const { total, submitOrder } = useOrderList();
  const [f, setF] = useState({
    fulfillment: "delivery",
    customer_name: "",
    phone: "",
    zone: "",
    address: "",
    delivery_time: "En kısa sürede",
    payment: "cash",
    order_note: "",
    kvkk: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const outOfZone = f.fulfillment === "delivery" && f.zone === "__other__";
  const isDelivery = f.fulfillment === "delivery";

  const t = computeTotals(total, f.fulfillment);
  const pickupT = computeTotals(total, "pickup");
  const belowMin = isDelivery && t.subtotal > 0 && t.subtotal < DELIVERY_MIN_TOTAL;

  const valid =
    f.customer_name.trim().length >= 3 &&
    f.phone.replace(/\D/g, "").length >= 10 &&
    f.payment &&
    f.kvkk &&
    !belowMin &&
    (!isDelivery || (f.zone && f.zone !== "__other__" && f.address.trim().length >= 10));

  // Buton pasifken kullanıcıya eksikleri göstermek için (mobil UX).
  const missing = [];
  if (f.customer_name.trim().length < 3) missing.push("Ad Soyad");
  if (f.phone.replace(/\D/g, "").length < 10) missing.push("Telefon");
  if (isDelivery) {
    if (!f.zone) missing.push("Teslimat bölgesi");
    else if (f.zone !== "__other__" && f.address.trim().length < 10) missing.push("Açık adres");
  }
  if (!f.kvkk) missing.push("KVKK onayı");

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      const res = await submitOrder(site, {
        ...f,
        zone: isDelivery ? f.zone : "",
        address: isDelivery ? f.address.trim() : "",
        customer_name: f.customer_name.trim(),
        phone: f.phone.trim(),
        order_note: f.order_note.trim(),
      });
      if (res?.ok) {
        toast.success(`Siparişiniz alındı${res.orderNo ? ` · ${res.orderNo}` : ""}`, {
          description: "WhatsApp üzerinden onaylayabilirsiniz.",
        });
        onDone();
      } else {
        toast.error("Sipariş gönderilemedi, tekrar deneyin.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div data-testid="checkout-form" className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <button
          data-testid="checkout-back"
          onClick={onBack}
          className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-cream/50 transition-colors hover:text-copper"
        >
          <ChevronLeft className="h-4 w-4" /> Listeye dön
        </button>

        {/* Teslimat türü */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { k: "delivery", label: "Adrese Teslim", icon: Truck },
            { k: "pickup", label: "Dükkândan Al", icon: Store },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              data-testid={`checkout-fulfillment-${k}`}
              onClick={() => set("fulfillment", k)}
              className={`relative flex items-center justify-center gap-2 rounded-sm border px-3 py-2.5 text-sm transition-colors ${
                f.fulfillment === k
                  ? "border-copper bg-copper/15 text-cream"
                  : "border-copper/20 text-cream/60 hover:border-copper/50"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {k === "pickup" && (
                <span className="absolute -right-1.5 -top-2 rounded-full bg-[#25D366] px-1.5 py-0.5 text-[9px] font-bold text-charcoal">
                  %20
                </span>
              )}
            </button>
          ))}
        </div>
        {f.fulfillment === "pickup" ? (
          <div
            data-testid="pickup-discount-applied"
            className="flex items-center gap-3 rounded-sm border border-copper/50 bg-gradient-to-r from-copper/20 via-copper/[0.06] to-transparent px-4 py-3"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-copper/50 bg-copper/15 font-serif text-sm font-bold text-copper">
              %{PICKUP_DISCOUNT_PCT}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight text-cream">Dükkândan Al indirimi uygulandı</p>
              {pickupT.discountAmount > 0 && (
                <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-copper/85">
                  ≈ {formatPrice(pickupT.discountAmount)} tasarruf ediyorsunuz
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] text-cream/45">
              Adrese teslim için minimum sepet tutarı {formatPrice(DELIVERY_MIN_TOTAL)}.
            </p>
            {pickupT.discountAmount > 0 && (
              <button
                type="button"
                data-testid="switch-to-pickup"
                onClick={() => set("fulfillment", "pickup")}
                className="flex w-full items-center gap-2.5 rounded-sm border border-copper/40 bg-copper/5 px-3 py-2.5 text-left transition-colors hover:bg-copper/10"
              >
                <Store className="h-4 w-4 flex-shrink-0 text-copper" />
                <span className="text-xs leading-tight text-cream/85">
                  <b className="text-copper">Dükkândan alın, %{PICKUP_DISCOUNT_PCT} kazanın</b> — ≈ {formatPrice(pickupT.discountAmount)} indirim
                </span>
              </button>
            )}
          </div>
        )}
        {belowMin && (
          <div
            data-testid="checkout-min-warning"
            className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Adrese teslim minimumu {formatPrice(DELIVERY_MIN_TOTAL)}. Sepetiniz {formatPrice(t.subtotal)}.
              {" "}<b>{formatPrice(DELIVERY_MIN_TOTAL - t.subtotal)}</b> daha ekleyin ya da <b>Dükkândan Al</b> seçin (%20 indirim).
            </span>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Ad Soyad *</label>
            <input
              data-testid="checkout-name"
              value={f.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
              placeholder="Adınız Soyadınız"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Telefon *</label>
            <input
              data-testid="checkout-phone"
              value={f.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="05xx xxx xx xx"
              inputMode="tel"
              className={inputCls}
            />
          </div>

          {isDelivery && (
            <>
              <div>
                <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Teslimat Bölgesi *</label>
                <select
                  data-testid="checkout-zone"
                  value={f.zone}
                  onChange={(e) => set("zone", e.target.value)}
                  className={inputCls}
                >
                  <option value="">Semt seçiniz</option>
                  {SERVICE_AREAS.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                  <option value="__other__">Bölgem listede yok / Diğer</option>
                </select>
              </div>

              {outOfZone ? (
                <div
                  data-testid="checkout-zone-warning"
                  className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Şu an sadece Beşiktaş–Gayrettepe çevresine teslimat yapıyoruz. Bölgeniz için lütfen
                    WhatsApp'tan bizimle görüşün ya da <b>Dükkândan Al</b> seçeneğini kullanın.
                  </span>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Açık Adres *</label>
                  <textarea
                    data-testid="checkout-address"
                    value={f.address}
                    onChange={(e) => set("address", e.target.value)}
                    placeholder="Mahalle, cadde, bina no, daire, tarif…"
                    rows={2}
                    className={`${inputCls} h-auto py-2`}
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">
              {isDelivery ? "Teslimat Zamanı" : "Alım Zamanı"}
            </label>
            <select
              data-testid="checkout-time"
              value={f.delivery_time}
              onChange={(e) => set("delivery_time", e.target.value)}
              className={inputCls}
            >
              {DELIVERY_TIME_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Ödeme */}
          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Ödeme Yöntemi *</label>
            <div className="grid grid-cols-2 gap-2">
              {PAYMENT_OPTIONS.map((p) => {
                const Icon = p.key === "card" ? CreditCard : Banknote;
                return (
                  <button
                    key={p.key}
                    data-testid={`checkout-payment-${p.key}`}
                    onClick={() => set("payment", p.key)}
                    className={`flex items-center justify-center gap-2 rounded-sm border px-2 py-2.5 text-xs transition-colors ${
                      f.payment === p.key
                        ? "border-copper bg-copper/15 text-cream"
                        : "border-copper/20 text-cream/60 hover:border-copper/50"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-cream/50">Sipariş Notu</label>
            <textarea
              data-testid="checkout-note"
              value={f.order_note}
              onChange={(e) => set("order_note", e.target.value)}
              placeholder="Genel not (örn. zili çalmayın, kapıda arayın)"
              rows={2}
              className={`${inputCls} h-auto py-2`}
            />
          </div>
        </div>
      </div>

      <div className="border-t border-dashed border-copper/30 bg-stone-light px-5 py-4">
        {t.subtotal > 0 && (
          <div className="mb-3 space-y-1">
            {t.discountAmount > 0 && (
              <>
                <div className="flex justify-between text-xs text-cream/55">
                  <span>Ara toplam</span><span>{formatPrice(t.subtotal)}</span>
                </div>
                <div className="flex justify-between text-xs text-[#25D366]">
                  <span>Dükkândan Al indirimi (%{t.discountPct})</span>
                  <span>-{formatPrice(t.discountAmount)}</span>
                </div>
              </>
            )}
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-xs uppercase tracking-wider text-cream/60">Tahmini Toplam</span>
              <span className="font-serif text-2xl leading-none text-copper">{formatPrice(t.total)}</span>
            </div>
          </div>
        )}
        {/* KVKK — sabit alt panelde, gönder butonuyla birlikte her zaman görünür */}
        <label className="mb-3 flex items-start gap-2 rounded-sm border border-copper/25 bg-charcoal/40 px-3 py-2.5 text-xs text-cream/75">
          <input
            type="checkbox"
            data-testid="checkout-kvkk"
            checked={f.kvkk}
            onChange={(e) => set("kvkk", e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-copper"
          />
          <span>Siparişimin işlenmesi için bilgilerimin kullanılmasını kabul ediyorum (KVKK).</span>
        </label>
        {!valid && !busy && !belowMin && missing.length > 0 && (
          <p data-testid="checkout-missing-hint" className="mb-2 text-center text-[11px] text-amber-300/90">
            Tamamlayın: {missing.join(" · ")}
          </p>
        )}
        <button
          data-testid="checkout-submit"
          onClick={submit}
          disabled={!valid || busy}
          className="flex w-full items-center justify-center gap-2.5 rounded-sm bg-[#25D366] px-5 py-3.5 text-base font-bold uppercase tracking-wide text-charcoal transition-colors hover:bg-[#1eb858] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          Siparişi Gönder
        </button>
        <p className="mt-2.5 text-center font-mono text-[11px] text-cream/45">
          Tahmini tutar · kesimde tartılarak kesinleşir · Online ödeme yok
        </p>
      </div>
    </>
  );
}
